import { useEffect, useState } from 'react';
import { User, UserPlan } from '@/models';
import { API_BASE } from '@/config.ts';
import { FaBullhorn, FaClock, FaRocket, FaTimes } from 'react-icons/fa';
import { Button } from '@/components/ui/button.tsx';
import { Link } from 'react-router-dom';
import { useBreakpoint } from '@/hooks/use-breakpoint.ts';

type Props = {
  userId: string | undefined;
};

export function ZeroPlanPopup({ userId }: Props) {
  const [showZeroPopup, setShowZeroPopup] = useState(false);
  const [resolvedPlan, setResolvedPlan] = useState<UserPlan | null>(null);
  const [loadedUser, setLoadedUser] = useState<User | null>(null);
  const { isMd: isDesktop } = useBreakpoint('md');

  const getDaysUntilAds = (): number | null => {
    if (!loadedUser?.createdAt) {
      return null;
    }

    const created = new Date(loadedUser.createdAt);
    const adsDate = new Date(created);

    adsDate.setMonth(adsDate.getMonth() + 1);

    const diff = adsDate.getTime() - Date.now();

    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const daysLeft = getDaysUntilAds();
  const adsStarted = daysLeft !== null && daysLeft <= 0;

  useEffect(() => {
    if (!loadedUser) return;

    if (resolvedPlan === UserPlan.FREE) {
      setShowZeroPopup(true);
    }
  }, [resolvedPlan, loadedUser]);

  useEffect(() => {
    if (!userId) return;

    let mounted = true;

    const loadUser = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/users/${userId}/full`);

        if (!res.ok) return;

        const data = await res.json();
        setLoadedUser(data);

        if (!mounted) return;

        if (data?.plan) {
          setResolvedPlan(data.plan);
        }
      } catch (e) {
        console.error('Failed loading user:', e);
      }
    };

    loadUser();

    return () => {
      mounted = false;
    };
  }, [userId]);

  if (!loadedUser) {
    return null;
  }

  if (!showZeroPopup) {
    return null;
  }

  // TODO: remove
  if (!isDesktop) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div className="relative flex max-h-[95vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-neutral-900 shadow-2xl">
        <button
          onClick={() => setShowZeroPopup(false)}
          className="absolute right-4 top-4 z-20 text-neutral-400 transition hover:text-white"
        >
          <FaTimes size={20} />
        </button>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto md:flex-row">
          <div className="flex flex-1 flex-col justify-center p-6 md:p-10">
            <div className="mb-4 flex items-center gap-3">
              <FaRocket size={28} color="white" />
              <span className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
                ZERO
              </span>
            </div>

            <h2 className="mb-4 text-3xl font-bold text-white md:text-4xl">
              {adsStarted ? 'Голосовая реклама активна' : 'У вас тариф ZERO'}
            </h2>

            <p className="mb-6 text-base text-neutral-300 md:text-lg">
              Бесплатный тариф предоставляет доступ к основному функционалу платформы
            </p>

            <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 p-4">
              <div className="mb-2 flex items-center gap-2 text-amber-300">
                <FaBullhorn />
                <span className="font-semibold">О тарифе</span>
              </div>

              {!adsStarted ? (
                <div className="flex flex-wrap items-center gap-2 text-lg font-semibold md:text-xl">
                  <FaClock />
                  До начала голосовой рекламы осталось <b>{daysLeft}</b> дн.
                </div>
              ) : (
                <div className="text-lg font-semibold text-white">
                  На вашем тарифе ZERO предусмотрена голосовая реклама во время звонков
                </div>
              )}

              <div className="mt-2 text-sm text-neutral-300">
                Голосовые интеграции позволяют поддерживать работу бесплатного тарифа без
                ограничений по основным функциям
              </div>
            </div>
          </div>

          <div className="relative h-56 shrink-0 md:h-auto md:w-[42%]">
            <img
              src="/assets/ads/koala-gamer-lvl.jpg"
              alt="ZERO"
              className="h-full w-full object-cover"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/60 to-transparent md:bg-gradient-to-l md:from-transparent md:to-neutral-900/80" />
          </div>
        </div>

        <div className="flex shrink-0 flex-col gap-3 border-t border-white/10 p-4 sm:flex-row">
          <Button
            onClick={() => setShowZeroPopup(false)}
            variant="secondary"
            className="w-full sm:w-auto"
          >
            Продолжить
          </Button>

          <Button asChild className="w-full sm:w-auto" onClick={() => setShowZeroPopup(false)}>
            <Link to="/channels/@me/boost">Отключить рекламу</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
